import { UserServiceClient } from 'fitbeat.api-clients';
import { calculateTimelineText, isTop3 } from 'fitbeat.gym-core';
import { IUserLeaderboardViewModel } from 'fitbeat.models';
import React, { FunctionComponent, useEffect } from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator, ScrollView, StyleSheet, Text, View,
} from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';
import { connect } from 'react-redux';
import { TranslateProp } from '../../src/common-types.js';
import MedalStarIcon from '../assets/icons/medal-star.svg';
import { Icon } from '../components';
import Label from '../components/Label';
import { getApiConfig } from '../config';
import { IAppStore } from '../HomeWorkouts';
import { GoogleAnalytics } from '../services/google-analytics';
import { leaderboardSubHeading } from '../SummaryDashboard/components/LeaderboardOverview';
import Theme from '../theme';
import { loadInactiveLeaderboardsForUser as loadInactiveLeaderboardsForUserAction } from '../Tracking/actions';
import { rankColorMapping } from '../Tracking/components/LeaderboardPositionIndicator';
import LeaderboardOverviewPosition from '../Tracking/components/leaderboards/LeaderboardOverviewPosition';
import { completedLeaderboardsSelector } from '../Tracking/selectors';
import { defaultScale, Logger } from '../utils';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import { isWeb } from '../utils/ScreenSizes';

interface IProps {
  t: TranslateProp;
  navigation: any;
  loadInactiveLeaderboardsForUser: () => IUserLeaderboardViewModel[];
  isLoadingInactiveLeaderboards: boolean;
  completedLeaderboards: IUserLeaderboardViewModel[];
}

const CompletedLeaderboard: FunctionComponent<IProps> = (
  {
    t,
    navigation,
    loadInactiveLeaderboardsForUser,
    isLoadingInactiveLeaderboards,
    completedLeaderboards,
  },
) => {
  useEffect(() => {
    loadInactiveLeaderboardsForUser();
  }, []);

  if (isLoadingInactiveLeaderboards) {
    return (
      <ActivityIndicator
        size='large'
        animating
        color={Theme.PRIMARY}
        style={styles.loader}
      />
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scrollViewContainer}>
        {completedLeaderboards && completedLeaderboards.map((leaderboard: IUserLeaderboardViewModel, index: number) => (
          <TouchableOpacity
            key={`${leaderboard.leaderboardId}-${leaderboard.startDate}-${leaderboard.endDate}`}
            onPress={() => navigation.navigate('LeaderboardStats', { leaderboard })}
          >
            <View style={styles.cardContainer}>
              <View style={styles.cardHeadingContainer}>
                <Text style={styles.cardHeading}>{leaderboard.name}</Text>
                <Icon
                  size={defaultScale(18)}
                  color={Theme.GREY_DARK_MEDIUM}
                  name='ios-arrow-forward'
                />
              </View>
              <Text style={styles.cardSubHeading}>{leaderboardSubHeading(leaderboard, t)}</Text>
              <View style={styles.cardBodyContainer}>
                <View style={styles.iconPositionContainer}>
                  <MedalStarIcon
                    color={
                          leaderboard?.position && isTop3(leaderboard.position)
                            ? rankColorMapping[leaderboard.position]
                            : Theme.GREY_MEDIUM
                        }
                    style={isWeb() ? { marginRight: defaultScale(12) } : styles.medalIcon}
                  />
                  <LeaderboardOverviewPosition leaderboard={leaderboard} t={t} />
                </View>
                <View style={styles.timelineTextContainer}>
                  {index === 0 && (
                  <Label
                    text={t('tracking:featured')}
                    alignment='flex-end'
                    containerStyle={{ marginVertical: defaultScale(5) }}
                  />
                  )}
                  <Text style={styles.timelineText}>
                    {calculateTimelineText(leaderboard.startDate, leaderboard.endDate, leaderboard.timezone)}
                  </Text>
                </View>
              </View>
            </View>
          </TouchableOpacity>
        ))}
        {!completedLeaderboards
            && (
              <View style={styles.noCompletedLeaderboardsContainer}>
                <Text style={styles.noCompletedLeaderboardText}>{t('tracking:noCompletedLeaderboards')}</Text>
              </View>
            )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.GREY_LIGHT,
    flex: 1,
  },
  scrollViewContainer: {
    flexGrow: 1,
  },
  cardContainer: {
    backgroundColor: Theme.WHITE,
    padding: defaultScale(15),
    marginTop: defaultScale(10),
  },
  cardHeadingContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: defaultScale(Theme.textStyle.heading2.fontSize * 2 + 10),
  },
  cardHeading: {
    ...Theme.textStyle.heading2,
  },
  cardSubHeading: {
    ...Theme.textStyle.bodyCopyReg,
    marginTop: defaultScale(5),
    marginBottom: defaultScale(10),
  },
  cardBodyContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  medalIcon: {
    marginRight: defaultScale(12),
  },
  iconPositionContainer: {
    flexDirection: 'row',
    flex: 1,
    alignItems: 'flex-end',
  },
  timelineTextContainer: {
    justifyContent: 'flex-end',
  },
  timelineText: {
    ...Theme.textStyle.small,
  },
  loader: {
    height: defaultScale(300),
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: defaultScale(20),
    alignSelf: 'center',
  },
  noCompletedLeaderboardsContainer: {
    paddingHorizontal: defaultScale(60),
    marginTop: defaultScale(20),
  },
  noCompletedLeaderboardText: {
    ...Theme.textStyle.heading2,
    color: Theme.GREY_MEDIUM,
    textAlign: 'center',
  },
});

const mapStateToProps = (state: IAppStore) => {
  const { isLoadingInactiveLeaderboards } = state.tracking.view;
  const completedLeaderboards = completedLeaderboardsSelector(state);
  return {
    isLoadingInactiveLeaderboards,
    completedLeaderboards,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  const userServiceClient = new UserServiceClient(
    getApiConfig(),
    fetch,
    Logger.debug,
    new AuthTokenBuilder(),
    GoogleAnalytics,
  );
  return {
    loadInactiveLeaderboardsForUser: () => dispatch(
      loadInactiveLeaderboardsForUserAction(userServiceClient, 'all'),
    ),
  };
};

export const CompletedLeaderboardScreen = connect(mapStateToProps, mapDispatchToProps)(translate(
  ['dashboard', 'stats'],
  { wait: true },
)(CompletedLeaderboard));
